<script lang="ts" module>
	import type { MediaSrc } from "@components/common/MediaSrc.js";
	import type { ClassValue } from "svelte/elements";
	import youtubeIcon from "@core/assets/icons/youtube.svg?raw";
	import type { Media } from "@core/schema/Media.js";
	import type { SourceSetQuery } from "@core/schema/SourceSetQuery.js";
	import { querySizeMappings } from "@core/schema/querySizeMappings.js";
	import { getThumbnailURLFromYoutubeURL } from "@core/schema/utils/getThumbnailURLFromYoutubeURL.js";
	import { parseSourceSet } from "@core/schema/utils/parseSourceSet.js";
	import { sortSourceSetQuery } from "@core/schema/utils/sortSourceSetQuery.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { lazyLoadVideo } from "@core/utils/lazyLoadVideo.js";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import Icon from "./Icon.svelte";
	import Lightbox from "./Lightbox.svelte";

	type Sourceset = string | Media | [number, number] | [number, number, Media];
	interface ResolvedSource {
		srcset: string;
		type: string;
	}
	interface Content {
		kind: "video" | "picture";
		src: string;
		type: string;
		media: Media | null;
	}
	interface Preload {
		href: string;
		type: string;
		media: string;
	}

	function resolveSource(
		set: Sourceset,
		type: string,
		parentMedia: Media | null,
		bucketUrlMap: Parameters<typeof getUploadedFilePath>[1],
	): ResolvedSource | null {
		if (typeof set === "string") {
			return { srcset: type.startsWith("video") ? `${set}#t=0.001` : set, type };
		}
		if (Array.isArray(set)) {
			const [height, width, media] = set;
			if (media !== undefined) {
				return media.type.startsWith("image")
					? { srcset: thumbUploadedFile(width, height, media, bucketUrlMap), type: "image/webp" }
					: { srcset: getUploadedFilePath(media, bucketUrlMap), type: media.type };
			}
			if (parentMedia?.type.startsWith("image")) {
				return { srcset: thumbUploadedFile(width, height, parentMedia, bucketUrlMap), type: "image/webp" };
			}
			return null;
		}
		return { srcset: getUploadedFilePath(set, bucketUrlMap), type: set.type };
	}

	function negateQuery(query: SourceSetQuery): SourceSetQuery {
		return `${query.startsWith(">") ? "<" : ">"}${query.slice(1)}` as SourceSetQuery;
	}

	function getQuerySize(query: SourceSetQuery): number {
		return querySizeMappings[query.slice(1) as keyof typeof querySizeMappings];
	}

	function intersectQueries(queries: SourceSetQuery[]): string | null {
		const lowerBounds = queries.filter((query) => query.startsWith(">"));
		const upperBounds = queries.filter((query) => query.startsWith("<"));
		const [lowerBound] = lowerBounds.toSorted((first, second) => getQuerySize(second) - getQuerySize(first));
		const [upperBound] = upperBounds.toSorted((first, second) => getQuerySize(first) - getQuerySize(second));
		if (lowerBound !== undefined && upperBound !== undefined && getQuerySize(lowerBound) >= getQuerySize(upperBound)) {
			return null;
		}
		return [lowerBound, upperBound]
			.filter((query) => query !== undefined)
			.map(parseSourceSet)
			.join(" and ");
	}
</script>

<script lang="ts">
	// eslint-disable-next-line svelte/no-unused-props
	const all: {
		width: number;
		height: number;
		alt: string;
		eager?: boolean;
		autoplay?: boolean;
		loop?: boolean;
		muted?: boolean;
		playsInline?: boolean;
		noControls?: boolean;
		class?: ClassValue;
		draggable?: boolean;
		wrapperClass?: ClassValue;
		poster?: Media | string;
		sourcesets: Partial<Record<SourceSetQuery, Sourceset>>;
		lightbox?: boolean | string;
		lightboxClass?: ClassValue;
		lightboxWidth?: number;
		preload?: "low" | "high";
		source: MediaSrc;
	} = $props();

	const {
		class: customClass,
		wrapperClass,
		eager = false,
		draggable,
		autoplay,
		height,
		width,
		alt,
		loop = false,
		muted = false,
		playsInline = true,
		noControls = false,
		sourcesets,
		lightbox,
		lightboxClass,
		lightboxWidth = 1920,
		preload,
	} = $derived(all);

	const { FILE_BUCKET_URL_MAP } = getGlobalSettings();

	const sortedSourceset = $derived(
		Object.entries(sourcesets).toSorted(([firstQuery], [secondQuery]) =>
			sortSourceSetQuery(firstQuery as SourceSetQuery, secondQuery as SourceSetQuery),
		) as [SourceSetQuery, Sourceset][],
	);

	const content = $derived.by((): Content | null => {
		if ("src" in all.source) {
			if (all.source.type.startsWith("video")) {
				return { kind: "video", src: all.source.src, type: all.source.type, media: null };
			}
			return all.source.type ? { kind: "picture", src: all.source.src, type: all.source.type, media: null } : null;
		}
		if ("youtube" in all.source) {
			return { kind: "picture", src: getThumbnailURLFromYoutubeURL(all.source.youtube), type: "", media: null };
		}
		return {
			kind: all.source.type.startsWith("video") ? "video" : "picture",
			src: getUploadedFilePath(all.source, FILE_BUCKET_URL_MAP),
			type: all.source.type,
			media: all.source,
		};
	});

	const resolvedSources = $derived(
		content === null
			? []
			: sortedSourceset.flatMap(([query, set]) => {
					const source = resolveSource(set, content.type, content.media, FILE_BUCKET_URL_MAP);
					return source ? [{ query, ...source }] : [];
				}),
	);

	const preloads = $derived.by((): Preload[] => {
		if (preload === undefined || content?.kind !== "picture") {
			return [];
		}
		const candidates = [
			...resolvedSources.map(({ query, srcset, type }, index) => ({
				href: srcset,
				type,
				queries: [query, ...resolvedSources.slice(0, index).map((previous) => negateQuery(previous.query))],
			})),
			{
				href: content.src,
				type: content.type,
				queries: resolvedSources.map((previous) => negateQuery(previous.query)),
			},
		];
		return candidates.flatMap(({ href, type, queries }) => {
			const media = intersectQueries(queries);
			return media === null ? [] : [{ href, type, media }];
		});
	});

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet sources()}
	{#each resolvedSources as { query, srcset, type } (query)}
		<source {srcset} {type} media={parseSourceSet(query)} />
	{/each}
{/snippet}

{#snippet video(src: string, type: string)}
	{@const poster =
		all.poster !== undefined
			? typeof all.poster === "string"
				? all.poster
				: thumbUploadedFile(1920, 0, all.poster, FILE_BUCKET_URL_MAP)
			: undefined}

	<video
		use:lazyLoadVideo={{ enabled: !eager, autoplay }}
		autoplay={autoplay && eager}
		{height}
		{width}
		{loop}
		{muted}
		{draggable}
		playsinline={playsInline}
		controls={!noControls}
		class={[customClass, wrapperClass]}
		{poster}
		preload={eager ? undefined : "none"}
	>
		{@render sources()}

		<source src="{src}#t=0.001" {type} />
	</video>
{/snippet}

{#snippet picture(src: string)}
	<picture class={wrapperClass}>
		{@render sources()}

		<img {draggable} class={customClass} {alt} {src} {width} {height} loading={eager ? "eager" : "lazy"} />
	</picture>
{/snippet}

{#snippet root()}
	{#if content?.kind === "video"}
		{@render video(content.src, content.type)}
	{:else if content?.kind === "picture"}
		{@render picture(content.src)}
	{/if}
{/snippet}

<svelte:head>
	{#each preloads as { href, type, media } (media)}
		<link rel="preload" as="image" {href} type={type || undefined} media={media || undefined} fetchpriority={preload} />
	{/each}
</svelte:head>

{#if lightbox}
	{@const id = typeof lightbox === "string" ? lightbox : undefined}
	{@const caption = alt}

	<Lightbox
		class={[lightboxClass, "relative"]}
		source={"youtube" in all.source
			? { href: all.source.youtube.toString() }
			: "src" in all.source
				? { href: all.source.src }
				: { media: all.source, width: lightboxWidth }}
		{id}
		{caption}
		type={"youtube" in all.source ? "youtube" : all.source.type.startsWith("video") ? "video" : "image"}
	>
		{@render root()}
		{#if "youtube" in all.source}
			<div
				class="absolute inset-0 overflow-hidden transition-[filter,transform] [a:hover>&]:scale-110 [a:hover>&]:brightness-150"
			>
				<div class="absolute bg-white left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-1/6"></div>
				<Icon
					icon={youtubeIcon}
					class={[
						"w-1/5 text-[#FF0201] absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
						"transition-[filter,transform]",
					]}
				/>
			</div>
		{/if}
	</Lightbox>
{:else}
	{@render root()}
{/if}
